import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import { AuthType, PermissionsDialog, PermissionsDialogProps } from '../../app/permissions/PermissionsDialog';
import { PermissionsDialogStyles } from '../../app/permissions/style/PermissionsDialogStyle';
import { Formatters, Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { PermissionsEditabilityLogicFactory } from '../helpers/permissions/PermissionsEditabilityLogicFactory';
import { L10n } from '../L10n/L10n';
import {
  DatabaseContentType,
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  TableContentType,
  ViewContentType,
  WorkbookContentType,
} from '../models/ContentTypes';
import { MetricContentType } from '../../metrics/models/MetricContentType';
import { IDataSourceInfo } from '../models/IDataSourceInfo';
import { PermissionRoles } from '../models/PermissionRoles';
import { getPermissionsPanelColumnWidths } from '../models/PermissionsPanelColumnWidths';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Databases } from '../resources/Databases';
import { ExplicitPermissions } from '../resources/ExplicitPermissions';
import { Projects } from '../resources/Projects';
import { SiteUsers } from '../resources/SiteUsers';
import { IGetActionableObjectsResult } from '../ServerApi/requests/GetActionableObjectsRequest';
import { INamedItem } from '../ServerApi/types/resourceParts';
import { ServerService } from '../services/ServerService';
import { EffectivePermissionsService } from './EffectivePermissionsService';
import { GetActionableObjectsForAction } from './GetActionableObjectsForAction';
import { LongRunningActionService } from './LongRunningActionService';
import { ModalService, ModalSize } from './ModalService';
import { ReactModalService } from './ReactModalService';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { ToasterService } from './ToasterService';

export interface IPermissionsDialogScope extends ng.IScope {
  authorizables: Set<any>;
  authorizableType: string;
  permissionsContainerWidth: number;
  permissionsPanelHeight: number;
  actionableItems: Set<INamedItem>;
  templateType: string;
  permissionsDialogProps: PermissionsDialogProps;
}

export class EditPermissionsAction {

  public static $inject = [
    'ModalService',
    '$q',
    '$window',
    '$translate',
    'GetActionableObjectsForAction',
    'ServerService',
    'ExplicitPermissions',
    'EffectivePermissionsService',
    'SiteUsers',
    'PermissionRoles',
    'PermissionsEditabilityLogicFactory',
    'Projects',
    'Databases',
    'LongRunningActionService',
    'SearchSuggestionsService',
  ];

  private static cannotViewAllSelectedMessageIdMap = (() => {
    const map: _.Dictionary<string> = {};
    map[DatabaseContentType.name] = 'permissions_cannotViewSelectedDatabases';
    map[DatasourceContentType.name] = 'permissions_cannotViewSelectedDataSources';
    map[FlowContentType.name] = 'permissions_cannotViewSelectedFlows';
    map[DataRoleContentType.name] = 'permissions_cannotViewSelectedDataRoles';
    map[ProjectContentType.name] = 'permissions_cannotViewSelectedProjects';
    map[TableContentType.name] = 'permissions_cannotViewSelectedTables';
    map[ViewContentType.name] = 'permissions_cannotViewSelectedViews';
    map[WorkbookContentType.name] = 'permissions_cannotViewSelectedWorkbooks';
    map[MetricContentType.name] = 'permissions_cannotViewSelectedMetrics';
    map[PublishedConnectionContentType.name] = 'permissions_cannotViewSelectedPublishedConnections';
    return map;
  })();

  constructor(
    private modalService: ModalService,
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private $translate: ng.translate.ITranslateService,
    private getActionableObjectsForAction: GetActionableObjectsForAction,
    private server: ServerService,
    private explicitPermissions: ExplicitPermissions,
    private effectivePermissionsService: EffectivePermissionsService,
    private siteUsers: SiteUsers,
    private permissionRoles: PermissionRoles,
    private permissionsEditabilityLogicFactory: PermissionsEditabilityLogicFactory,
    private projectsResource: Projects,
    private databasesResource: Databases,
    private longRunningActionService: LongRunningActionService,
    private searchSuggestionsService: SearchSuggestionsService,
  ) { }

  public showEditPermissionsDialog(items: Set<any>, contentType: string, actionContext: IActionContext): ng.IPromise<any> {

    const scope = <IPermissionsDialogScope>actionContext.scope.$new(true);

    if (contentType === DatasourceContentType.name && this.areAnyEmbeddedDatasourcesSelected(items)) {
      return this.$q.when(this.alertCantShowPermissions({
        translatedMessage: this.$translate.instant(
          'permissions_notShowingEmbeddedAndPublishedDataSources', { ITEM_COUNT: items.size() }, L10n.Interpolations.Count)
      }));
    }

    return this.getActionableObjectsForAction.editPermissions({
      type: contentType,
      ids: items.keys()
    }).then((result: IGetActionableObjectsResult) => {
      const ids = result.ids;
      const unactionableItems = items.clone();
      let actionableItems = new Set<INamedItem>((item: INamedItem) => item.id);
      _.each(ids, (id: string) => {
        const item = items.get(id);
        actionableItems.add(item);
        unactionableItems.remove(item);
      });

      // if there are any items in the section that the user isn't allowed to set permissions on,
      // we don't show the permissions panel
      if (unactionableItems.size() > 0) {
        return this.alertCantShowPermissions({
          translatedMessage: this.getCannotViewAllSelectedMessage(contentType, items)
        }).result;
      }

      const authorizables = actionableItems.clone();
      const authorizableType = contentType;
      const templateType = contentType;

      const flowsEnabled = this.server.flowsOnSiteEnabled();
      const widthLayout = getPermissionsPanelColumnWidths(templateType, flowsEnabled);
      const permissionsPanelHeight = this.calculatePermissionsPanelHeight();

      let permissionsContainerWidth;

      if (FeatureFlags.permissionsDialogReactEnabled) {
        permissionsContainerWidth = PermissionsDialogStyles.dialogContainerWidth;

        scope.permissionsDialogProps = {
          authorizables,
          authorizableType,
          flowsEnabled,
          permissionsContainerWidth,
          permissionsPanelHeight,
          explicitPermissions: this.explicitPermissions,
          effectivePermissionsService: this.effectivePermissionsService,
          siteUsers: this.siteUsers,
          permissionRoles: this.permissionRoles,
          permissionsEditabilityLogicFactory: this.permissionsEditabilityLogicFactory,
          projectsResource: this.projectsResource,
          databasesResource: this.databasesResource,
          toasterService: ToasterService.instance,
          longRunningActionService: this.longRunningActionService,
          searchSuggestionsService: this.searchSuggestionsService,
          serverService: this.server,
        };
      } else {
        permissionsContainerWidth = widthLayout.getPermissionsDialogWidth($(this.$window).width(), flowsEnabled);

        scope.authorizables = authorizables;
        scope.authorizableType = authorizableType;
        scope.permissionsContainerWidth = permissionsContainerWidth;
        scope.permissionsPanelHeight = permissionsPanelHeight;
        scope.actionableItems = actionableItems;
        scope.templateType = templateType;
      }
      return this.openPermissionsDialog(scope).result;
    }).finally(() => scope.$destroy());
  }

  public openPermissionsDialog(scope: IPermissionsDialogScope) {
    if (FeatureFlags.permissionsDialogReactEnabled) {
      const dialogTitle = this.getPermissionsDialogTitle(scope.permissionsDialogProps.authorizableType, scope.permissionsDialogProps.authorizables);
      return ReactModalService.open({
        testId: 'permissions-dialog-react',
        type: DialogType.NormalWithTitleBar,
        dialogWidth: scope.permissionsDialogProps.permissionsContainerWidth,
        dialogContent: React.createElement(PermissionsDialog, scope.permissionsDialogProps),
        showCloseButton: true,
        positioningBehavior: DialogPositioningBehavior.Centered,
        shouldReturnFocusOnClose: true,
        title: dialogTitle,
      });
    } else {
      return this.modalService.open({
        scope: scope,
        templateUrl: 'permissionsDialog.html',
        size: ModalSize.fitToContent,
      });
    }
  }

  public getPermissionsDialogTitle(authorizableType: string, authorizables: Set<any>): string {
    if (authorizables) {
      switch (authorizableType.toLowerCase()) {
        case AuthType.DATABASE.toLowerCase():
          return Messages.permissionsForDatabase(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.DATAROLE.toLowerCase():
          return Messages.permissionsForDataRole(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.DATASOURCE.toLowerCase():
          return Messages.permissionsForDataSource(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.FLOW.toLowerCase():
          return Messages.permissionsForFlow(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.METRIC.toLowerCase():
          return Messages.permissionsForMetric(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.PUBLISHED_CONNECTION.toLowerCase():
          return Messages.permissionsForPublishedConnection(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.PROJECT.toLowerCase():
          return Messages.permissionsForProject(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.TABLE.toLowerCase():
          return Messages.permissionsForTable(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.VIEW.toLowerCase():
          return Messages.permissionsForView(this.getLocParamsForTitleFromAuthorizables(authorizables));

        case AuthType.WORKBOOK.toLowerCase():
          return Messages.permissionsForWorkbook(this.getLocParamsForTitleFromAuthorizables(authorizables));
      }
    }

    return Messages.permissions();
  }

  public getLocParamsForTitleFromAuthorizables(authorizables: Set<any>) {
    const itemsCount = authorizables.size();
    return {
      ITEM_COUNT: itemsCount,
      ITEM_COUNT_LOC: Formatters.formatCount(itemsCount),
      ITEM_NAME: authorizables.first().name
    };
  }

  private alertCantShowPermissions(message: { messageId?: string, translatedMessage?: string }) {
    return this.modalService.alert(_.extend({
      titleId: 'permissions_cannotViewPermissions'
    }, message));
  }

  /**
   * Calculates a height to make the permissions panel fit in smaller screen sizes.
   * TODO: refactor the layout of the permissions panel so this isn't needed.
   */
  private calculatePermissionsPanelHeight(): number {
    const modalDialog = 24 + 18 + 2; // top padding, bottom padding, border (.tb-modal)
    const modalDialogTitle = 21 + 18; // height, bottom margin (.tb-dialog-title)

    const availableHeight = $(this.$window).height();
    return availableHeight - (modalDialog + modalDialogTitle + ModalService.MinimumSpaceBetweenDialogAndViewport);
  }

  private getCannotViewAllSelectedMessage(authorizableType: string, unactionableItems: Set<INamedItem>): string {
    return this.$translate.instant(
      EditPermissionsAction.cannotViewAllSelectedMessageIdMap[authorizableType],
      {
        ITEM_NAME: L10n.elideIfLong(unactionableItems.first().name),
        ITEM_COUNT: unactionableItems.size()
      },
      L10n.Interpolations.Count);
  }

  private areAnyEmbeddedDatasourcesSelected(datasources: Set<INamedItem>): boolean {
    return !!datasources.find((datasource: IDataSourceInfo) => !!datasource.workbook);
  }

}

Module.instance().service('EditPermissionsAction', EditPermissionsAction);
